/**
 * Submit a lab order to LIMS: the order, then its samples, then one test per
 * sample per assay. Resumable — pass `existingOrderId` to finish an order a
 * previous attempt left half-created; samples are found by
 * (order_id, sample_description, collection_datestamp) and tests by
 * (sample_id, assay_id), so a retry never duplicates either. Any failure
 * after the order exists is rethrown as a LabOrderSubmitError carrying the
 * order id to resume with.
 */
import { buildLabOrderSubmission, sampleIdentityKey, } from './lab-order-submission.js';
/**
 * Thrown when the order exists but creating its samples or tests failed.
 * Retry with `{ existingOrderId: error.orderId }`; nothing is duplicated.
 */
export class LabOrderSubmitError extends Error {
    constructor(orderId, cause) {
        const reason = cause instanceof Error ? cause.message : String(cause);
        super(`[lab-order] order ${orderId} exists but submitting its samples or tests failed: ${reason}. ` +
            `Retry with existingOrderId ${orderId} to finish without duplicates.`);
        this.name = 'LabOrderSubmitError';
        this.orderId = orderId;
        this.cause = cause;
    }
}
/** Entities per create request; the server rejects more than 1000. */
export const LAB_ORDER_CREATE_CHUNK = 500;
const PAGE_SIZE = 200;
export async function submitLabOrder(client, input, options = {}) {
    const submission = buildLabOrderSubmission(input);
    // 1. The order. On resume, getOrders throws if the id is unknown.
    let orderId;
    if (options.existingOrderId !== undefined) {
        await client.getOrders(options.existingOrderId);
        orderId = options.existingOrderId;
    }
    else {
        const created = await client.createOrders([{ attributes: submission.order }]);
        const id = created.data[0]?.id;
        if (id == null)
            throw new Error('[lab-order] LIMS did not return an id for the new order');
        orderId = id;
    }
    try {
        // 2. The batch number needs the order id; every sample below carries it.
        const batchNumber = submission.batchNumberFor(orderId);
        // 3. Samples: find-or-create by (order_id, sample_description, collection_datestamp).
        const existingSamples = new Map();
        await client.forSamples({ filters: [{ attribute_name: 'order_id', operator: '=', value: orderId }], limit: PAGE_SIZE }, (sample) => {
            const a = sample.attributes ?? {};
            if (sample.id == null || typeof a.sample_description !== 'string' || typeof a.collection_datestamp !== 'string')
                return;
            const key = sampleIdentityKey(a.sample_description, a.collection_datestamp);
            if (!existingSamples.has(key))
                existingSamples.set(key, sample.id);
        });
        const sampleIds = submission.samples.map((s) => existingSamples.get(sampleIdentityKey(s.attributes.sample_description, s.attributes.collection_datestamp)));
        const missingIndexes = sampleIds.flatMap((id, i) => (id === undefined ? [i] : []));
        const newSampleIds = await createInChunks(missingIndexes.map((i) => ({ attributes: { ...submission.samples[i].attributes, order_id: orderId, batch_number: batchNumber } })), (slice) => client.createSamples(slice), 'samples');
        newSampleIds.forEach((id, n) => { sampleIds[missingIndexes[n]] = id; });
        const resolvedSampleIds = sampleIds;
        // 4. Tests: find-or-create by (sample_id, assay_id).
        const existingTests = new Map();
        await client.forTests({
            filters: [{ attribute_name: 'sample_id', operator: 'IN', value: resolvedSampleIds }],
            exclude_attributes: ['raw_worksheet_data', 'worksheet_data', 'icp_xml_data'],
            limit: PAGE_SIZE,
        }, (test) => {
            const a = test.attributes ?? {};
            if (test.id == null || typeof a.sample_id !== 'number' || typeof a.assay_id !== 'number')
                return;
            const key = testKey(a.sample_id, a.assay_id);
            if (!existingTests.has(key))
                existingTests.set(key, test.id);
        });
        const wanted = [];
        submission.samples.forEach((s, i) => {
            for (const assayId of s.assayIds)
                wanted.push({ sampleId: resolvedSampleIds[i], assayId });
        });
        const missingTests = wanted.filter((t) => !existingTests.has(testKey(t.sampleId, t.assayId)));
        const newTestIds = await createInChunks(missingTests.map((t) => ({ attributes: { sample_id: t.sampleId, assay_id: t.assayId, lab_id: input.labId } })), (slice) => client.createTests(slice), 'tests');
        newTestIds.forEach((id, n) => existingTests.set(testKey(missingTests[n].sampleId, missingTests[n].assayId), id));
        return {
            orderId,
            batchNumber,
            sampleIds: resolvedSampleIds,
            testIds: wanted.map((t) => existingTests.get(testKey(t.sampleId, t.assayId))),
        };
    }
    catch (error) {
        throw error instanceof LabOrderSubmitError ? error : new LabOrderSubmitError(orderId, error);
    }
}
/** Create entities in slices the server accepts; returns their ids in input order. */
async function createInChunks(entities, create, what) {
    const ids = [];
    for (let start = 0; start < entities.length; start += LAB_ORDER_CREATE_CHUNK) {
        const slice = entities.slice(start, start + LAB_ORDER_CREATE_CHUNK);
        const created = await create(slice);
        if (created.data.length !== slice.length) {
            throw new Error(`LIMS created ${created.data.length} of ${slice.length} ${what}`);
        }
        for (const entity of created.data) {
            if (entity.id == null)
                throw new Error(`LIMS did not return an id for one of the new ${what}`);
            ids.push(entity.id);
        }
    }
    return ids;
}
function testKey(sampleId, assayId) {
    return `${sampleId}:${assayId}`;
}
